import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';

import { getEnrichmentStatusConfig } from '@/components/police/utils/policeUtils';
import { Input } from '@/components/ui/input';
import type {
  ReadPoliceDepartment,
  ReadPoliceDepartmentWithStats,
} from '@/services/api/generated/policeDepartmentsService';
import { useSearchPoliceDepartmentsQuery } from '@/services/api/policeDepartmentsService';

import { CreateDepartmentModal } from './CreateDepartmentModal';

interface PoliceDepartmentPickerProps {
  selectedDepartmentId?: string | null;
  onDepartmentSelect: (departmentId: string) => void;
  /** Called with full department object when selecting from search results */
  onDepartmentSelected?: (
    department: ReadPoliceDepartment | ReadPoliceDepartmentWithStats,
  ) => void;
  onDepartmentCreated?: (
    department: ReadPoliceDepartment | ReadPoliceDepartmentWithStats,
  ) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  /** Pre-fill the search input (e.g. from CMS import). Does not auto-select. */
  initialSearchQuery?: string;
}

export const PoliceDepartmentPicker = ({
  selectedDepartmentId,
  onDepartmentSelect,
  onDepartmentSelected,
  onDepartmentCreated,
  placeholder = 'Search for police department...',
  disabled = false,
  required = false,
  initialSearchQuery,
}: PoliceDepartmentPickerProps) => {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery ?? '');
  const [showResults, setShowResults] = useState(false);
  const hasAppliedInitialQuery = useRef(false);

  // Apply initialSearchQuery when it becomes available after mount
  useEffect(() => {
    if (hasAppliedInitialQuery.current) return;
    if (!initialSearchQuery) return;
    if (searchQuery) return; // Don't overwrite if user already typed
    hasAppliedInitialQuery.current = true;
    setSearchQuery(initialSearchQuery);
    setShowResults(true);
  }, [initialSearchQuery, searchQuery]);
  const [selectedDepartmentName, setSelectedDepartmentName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const { data: searchResults, isFetching } = useSearchPoliceDepartmentsQuery(
    { query: searchQuery },
    { skip: searchQuery.length < 2 },
  );

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setShowResults(true);

    // Clear selected department if user is typing a new search
    if (selectedDepartmentId && value !== selectedDepartmentName) {
      setSelectedDepartmentName('');
    }
  };

  const handleDepartmentSelect = (
    department: ReadPoliceDepartmentWithStats,
  ) => {
    setSearchQuery(department.name ?? '');
    setSelectedDepartmentName(department.name ?? '');
    setShowResults(false);
    onDepartmentSelect(department.id);
    onDepartmentSelected?.(department);
  };

  const handleDepartmentCreated = (
    department: ReadPoliceDepartment | ReadPoliceDepartmentWithStats,
  ) => {
    setSearchQuery(department.name ?? '');
    setSelectedDepartmentName(department.name ?? '');
    onDepartmentSelect(department.id);
    onDepartmentCreated?.(department);
  };

  const hasResults = searchResults && searchResults.length > 0;

  return (
    <div className="space-y-3">
      <div className="relative">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className="pl-10"
          />
        </div>

        {showResults && searchQuery.length >= 2 && (
          <div
            ref={resultsRef}
            className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto"
          >
            {isFetching ? (
              <div className="px-4 py-3 text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                  <span>Searching departments...</span>
                </div>
              </div>
            ) : hasResults ? (
              <>
                {searchResults.map((dept) => (
                  <div
                    key={dept.id}
                    onClick={() => handleDepartmentSelect(dept)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDepartmentSelect(dept);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Select ${dept.name}`}
                    className="cursor-pointer px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">
                          {dept.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {dept.state_name
                            ? `${dept.state_name} (${dept.state_code})`
                            : dept.state_code}
                          {dept.jurisdiction_type && (
                            <span className="ml-2 capitalize">
                              • {dept.jurisdiction_type.toLowerCase()}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {(() => {
                          const statusConfig = getEnrichmentStatusConfig(
                            dept.enrichment_status,
                          );
                          const variant = statusConfig?.variant || 'secondary';
                          const label = statusConfig?.label || 'Unknown';

                          return (
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                variant === 'default'
                                  ? 'bg-green-100 text-green-700'
                                  : variant === 'outline'
                                    ? 'bg-orange-100 text-orange-700'
                                    : variant === 'destructive'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500">
                No departments found for &quot;{searchQuery}&quot;
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Department Option */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>Don&apos;t see the right department?</span>
        <CreateDepartmentModal onDepartmentCreated={handleDepartmentCreated}>
          <button
            type="button"
            className="text-blue-600 hover:text-blue-700 font-medium underline"
          >
            Add New Department
          </button>
        </CreateDepartmentModal>
      </div>
    </div>
  );
};
